import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import { useState } from "react"
import {
  StockActionDialog,
  type StockActionSubmit,
} from "@/supplies/components/stock-action-dialog"
// Alias useSupplyStockFn: name starts with "use" and trips rules-of-hooks lint.
import {
  useSupplyStockFn as consumeSupplyStockFn,
  getSuppliesDashboardFn,
} from "@/supplies/rpc"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"

export const Route = createFileRoute("/_supplies-layout/supplies")({
  loader: () => getSuppliesDashboardFn(),
  component: SuppliesDashboard,
})

interface LowStockItem {
  id: number
  name: string
  sku: string | null
  unit: string
  quantityOnHand: number
  reorderLevel: number | null
  category?: { id: number; name: string } | null
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function SuppliesDashboard() {
  const { skuCount, totalUnits, lowStockItems } = Route.useLoaderData() as {
    skuCount: number
    totalUnits: number
    lowStockItems: LowStockItem[]
  }
  const queryClient = useQueryClient()
  const router = useRouter()
  const [useTarget, setUseTarget] = useState<LowStockItem | null>(null)
  const [useError, setUseError] = useState<string | null>(null)

  const useStock = useMutation({
    mutationFn: (input: {
      itemId: number
      quantity: number
      note: string | null
    }) =>
      consumeSupplyStockFn({
        data: {
          itemId: input.itemId,
          quantity: input.quantity,
          note: input.note,
        },
      }),
    onSuccess: async () => {
      setUseError(null)
      setUseTarget(null)
      await queryClient.invalidateQueries({ queryKey: ["supply-items"] })
      await queryClient.invalidateQueries({ queryKey: ["supply-movements"] })
      await queryClient.invalidateQueries({ queryKey: ["supplies-dashboard"] })
      await router.invalidate()
    },
    onError: (error) => setUseError(getErrorMessage(error)),
  })

  const isUsing = useStock.isPending

  function openUse(item: LowStockItem) {
    setUseError(null)
    setUseTarget(item)
  }

  async function handleUseSubmit(payload: StockActionSubmit) {
    if (isUsing || useTarget == null || payload.quantity == null) return
    setUseError(null)
    try {
      await useStock.mutateAsync({
        itemId: useTarget.id,
        quantity: payload.quantity,
        note: payload.note ?? null,
      })
    } catch {
      // surfaced via onError
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm">
            Supplies overview: SKUs, stock on hand, and low-stock alerts.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to="/supplies/receive">Receive stock</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/supplies/items">View items</Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardDescription>Active SKUs</CardDescription>
            <CardTitle className="text-3xl tabular-nums">{skuCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Total units on hand</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {totalUnits}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Low stock items</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {lowStockItems.length}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <div>
        <h2 className="mb-3 font-medium text-lg">Low stock</h2>
        {lowStockItems.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-muted-foreground text-sm">
              No items are at or below reorder level.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {lowStockItems.map((item) => (
              <Card key={item.id}>
                <CardHeader className="pb-2">
                  <CardDescription>
                    {item.category?.name ?? "Uncategorized"}
                    {item.sku ? ` · ${item.sku}` : null}
                  </CardDescription>
                  <CardTitle className="text-lg">{item.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm tabular-nums">
                    {item.quantityOnHand} {item.unit} on hand
                    {item.reorderLevel != null
                      ? ` (reorder at ${item.reorderLevel})`
                      : null}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => openUse(item)}
                    >
                      Use
                    </Button>
                    <Button variant="link" className="h-auto px-0" asChild>
                      <Link
                        to="/supplies/items/$itemId"
                        params={{ itemId: String(item.id) }}
                      >
                        View item
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {useTarget != null ? (
        <StockActionDialog
          open
          onOpenChange={(open) => {
            if (!open && !isUsing) {
              setUseTarget(null)
              setUseError(null)
            }
          }}
          mode="use"
          itemName={useTarget.name}
          quantityOnHand={useTarget.quantityOnHand}
          isSubmitting={isUsing}
          error={useError}
          onSubmit={handleUseSubmit}
        />
      ) : null}
    </div>
  )
}
